import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { MapContainer, TileLayer, Circle, CircleMarker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const blank = { name: "", address: "", lat: 14.5547, lng: 121.0244, radius_m: 200, active: true, work_type: "Office Works" };

function ClickPick({ onPick }) {
  useMapEvents({ click(e) { onPick(Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))); } });
  return null;
}
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (!isNaN(lat) && !isNaN(lng)) map.setView([lat, lng]); }, [lat, lng, map]);
  return null;
}
function MapPicker({ lat, lng, radius, onPick }) {
  const la = Number(lat) || 14.5547, ln = Number(lng) || 121.0244;
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 0); return () => clearTimeout(t); }, []);
  if (!show) return <div className="rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-400" style={{ height: 260 }} data-testid="site-map">Loading map…</div>;
  return (
    <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="site-map">
      <MapContainer center={[la, ln]} zoom={13} style={{ height: 260, width: "100%" }} scrollWheelZoom>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
        <ClickPick onPick={onPick} />
        <Recenter lat={la} lng={ln} />
        <CircleMarker center={[la, ln]} radius={6} pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 1 }} />
        <Circle center={[la, ln]} radius={Number(radius) || 0} pathOptions={{ color: "#2563eb", fillOpacity: 0.12 }} />
      </MapContainer>
    </div>
  );
}

export default function Sites() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/hris/geofences").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ ...g }); setOpen(true); };
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation unavailable");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: Number(pos.coords.latitude.toFixed(6)), lng: Number(pos.coords.longitude.toFixed(6)) })); toast.success("Location captured"); },
      () => toast.error("Location permission denied"));
  };
  const save = async () => {
    const body = { ...form, lat: Number(form.lat), lng: Number(form.lng), radius_m: Number(form.radius_m) };
    try {
      if (editing) { await api.put(`/hris/geofences/${editing.id}`, body); toast.success("Updated"); }
      else { await api.post("/hris/geofences", body); toast.success("Created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (g) => { if (!window.confirm(`Delete site "${g.name}"?`)) return; try { await api.delete(`/hris/geofences/${g.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Sites (Geofence)" subtitle="Define work sites with a centre and radius. Clock-in is blocked outside the fence." />
      <div className="flex justify-end mb-4"><Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="new-site-button"><Icons.Plus className="h-4 w-4 mr-1.5" /> New Site</Button></div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Site</TableHead><TableHead>Coordinates</TableHead><TableHead>Radius</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody data-testid="sites-table">
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No sites yet.</TableCell></TableRow>}
            {rows.map((g) => (
              <TableRow key={g.id} data-testid={`site-row-${g.id}`}>
                <TableCell><div className="flex items-center gap-2"><Icons.MapPin className="h-4 w-4 text-slate-400" /><div><div className="font-medium text-slate-800 text-sm">{g.name} <Badge variant="outline" className="ml-1 text-[10px]" data-testid={`site-work-type-badge-${g.id}`}>{g.work_type || "Office Works"}</Badge></div><div className="text-xs text-slate-500">{g.address}</div></div></div></TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{g.lat}, {g.lng}</TableCell>
                <TableCell className="text-sm">{g.radius_m} m</TableCell>
                <TableCell><Badge className={`${g.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"} text-[10px]`}>{g.active ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell className="text-right"><div className="flex gap-0.5 justify-end">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(g)} data-testid={`site-edit-${g.id}`}><Icons.Pencil className="h-4 w-4 text-slate-500" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(g)} data-testid={`site-delete-${g.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Site</DialogTitle><DialogDescription>Employees assigned to this site can only clock in within the radius.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Site Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="site-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Work Type (sets the Overtime deduction)</Label>
              <div className="grid grid-cols-2 gap-2">{[["Office Works", "1 hr off per 3 OT hrs"], ["Site Works", "1 hr off per 4 OT hrs"]].map(([v, d]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, work_type: v })} className={`text-left rounded-lg border p-2.5 ${ (form.work_type || "Office Works") === v ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`site-work-type-${v.split(" ")[0].toLowerCase()}`}>
                  <div className="text-sm font-medium">{v}</div><div className="text-[11px] text-slate-500">{d}</div></button>))}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Latitude</Label><Input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="site-lat" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Longitude</Label><Input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="site-lng" /></div>
            </div>
            <Button variant="outline" size="sm" onClick={useMyLocation} data-testid="site-use-location"><Icons.Crosshair className="h-4 w-4 mr-1.5" /> Use my current location</Button>
            {open && <MapPicker lat={form.lat} lng={form.lng} radius={form.radius_m} onPick={(la, ln) => { setForm((f) => ({ ...f, lat: la, lng: ln })); toast.success("Centre set from map"); }} />}
            <p className="text-[11px] text-slate-400">Tap the map to drop the geofence centre; the shaded circle shows the radius.</p>
            <div className="space-y-1.5"><Label className="text-xs">Radius (meters)</Label><Input type="number" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} data-testid="site-radius" /></div>
            <div className="flex items-center justify-between border border-slate-200 rounded-lg p-3"><div className="text-sm font-medium">Active</div><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="site-active" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={!form.name} className="bg-slate-900 hover:bg-slate-800" data-testid="site-save">{editing ? "Save" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
